import type { SVGProps } from 'react';

/** Visual state of the MyI status glyph — drives accent colour + animation. */
export type MyIState = 'idle' | 'thinking' | 'scanning' | 'done' | 'alert';

interface Palette {
  /** Rays + rings. */
  accent: string;
  /** Core (outer). */
  core: string;
  /** Core (mid) — the animated ring on `thinking` / `scanning` / `alert`. */
  mid: string;
  /** Core (inner highlight). */
  inner: string;
}

const PALETTE: Record<MyIState, Palette> = {
  idle: { accent: '#3b82f6', core: '#2563eb', mid: '#93c5fd', inner: '#e0f2fe' },
  thinking: { accent: '#3b82f6', core: '#2563eb', mid: '#93c5fd', inner: '#e0f2fe' },
  scanning: { accent: '#3b82f6', core: '#2563eb', mid: '#93c5fd', inner: '#e0f2fe' },
  done: { accent: '#22c55e', core: '#16a34a', mid: '#86efac', inner: '#dcfce7' },
  alert: { accent: '#f59e0b', core: '#d97706', mid: '#fcd34d', inner: '#fef3c7' },
};

// Ray geometry (64×64 viewBox). Cardinal rays are the bright N/E/S/W spokes;
// diagonals are the fainter corner spokes.
const CARDINAL_RAYS = [
  [32, 9, 32, 19],
  [32, 45, 32, 55],
  [9, 32, 19, 32],
  [45, 32, 55, 32],
] as const;
const DIAGONAL_RAYS = [
  [16, 16, 23, 23],
  [48, 16, 41, 23],
  [16, 48, 23, 41],
  [48, 48, 41, 41],
] as const;

export interface MyIIconProps extends SVGProps<SVGSVGElement> {
  /** Visual state. Defaults to `idle`. */
  state?: MyIState;
}

/**
 * MyI status glyph — a dark "coin" with a glowing core and radiating spokes.
 * One self-contained 64×64 SVG across all states; size it with `className`
 * (e.g. `h-7 w-7`).
 *
 * States:
 *  • `idle` / `done` — static.
 *  • `thinking` — a single arc sweeps just inside the coin rim; core pulses.
 *  • `scanning` — a smaller coin with short dashes orbiting it like a halo
 *    (core pulses). On a `#1e293b` / `bg-slate-800` surface the coin merges in
 *    and only the rays/core/halo show.
 *  • `alert` — the core blinks.
 */
export function MyIIcon({ state = 'idle', ...props }: MyIIconProps) {
  const p = PALETTE[state];
  const isScanning = state === 'scanning';
  // `scanning` uses a smaller coin so the arc can orbit OUTSIDE it (a halo);
  // `thinking` keeps the full-size coin with the arc sweeping just inside.
  const coinR = isScanning ? 24 : 32;
  const showArc = state === 'thinking' || isScanning;
  const arcR = isScanning ? 30 : 28;
  const pulseCore = state === 'thinking' || isScanning;
  return (
    <svg
      viewBox="0 0 64 64"
      width={64}
      height={64}
      fill="none"
      role="img"
      aria-label={`MyI ${state}`}
      {...props}
    >
      {/* Coin */}
      <circle cx="32" cy="32" r={coinR} fill="#1e293b" />

      {/* Spinning arc — thinking (single sweep, inside) / scanning (orbiting
          halo, multiple short dashes). pathLength normalises the dash. */}
      {showArc && (
        <circle
          cx="32"
          cy="32"
          r={arcR}
          stroke={p.accent}
          strokeWidth="2"
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray={isScanning ? '5 18' : '15 85'}
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 32 32"
            to="360 32 32"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>
      )}

      {/* Faint dashed inner ring — skipped on scanning to keep the halo clean. */}
      {!isScanning && (
        <circle
          cx="32"
          cy="32"
          r="11"
          stroke={p.accent}
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity="0.5"
        />
      )}

      {/* Rays — cardinals always; diagonals on every state except scanning. */}
      <g stroke={p.accent} strokeLinecap="round">
        {CARDINAL_RAYS.map(([x1, y1, x2, y2]) => (
          <line
            key={`c-${x1}-${y1}-${x2}-${y2}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            strokeWidth="1.5"
            opacity="0.7"
          />
        ))}
        {!isScanning &&
          DIAGONAL_RAYS.map(([x1, y1, x2, y2]) => (
            <line
              key={`d-${x1}-${y1}-${x2}-${y2}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              strokeWidth="1"
              opacity="0.35"
            />
          ))}
      </g>

      {/* Core */}
      <circle cx="32" cy="32" r="7" fill={p.core} />
      <circle cx="32" cy="32" r="3.5" fill={p.mid}>
        {pulseCore && (
          <animate attributeName="r" values="3.5;4.6;3.5" dur="1.8s" repeatCount="indefinite" />
        )}
        {state === 'alert' && (
          <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" />
        )}
      </circle>
      <circle cx="32" cy="32" r="1.5" fill={p.inner} />
    </svg>
  );
}
